"use client";

import { useCallback, useRef, useState } from "react";
import {
  Room,
  Track,
  createLocalAudioTrack,
  createLocalScreenTracks,
  LocalVideoTrack,
  type LocalTrack
} from "livekit-client";

type BroadcastToken = {
  breakoutRoomId: string;
  livekitRoomName: string;
  participantToken: string;
};

type BroadcastStartPayload = {
  serverUrl: string;
  tokens: BroadcastToken[];
};

async function postBroadcast(
  roomName: string,
  sessionId: string,
  type: string
): Promise<{ serverUrl?: string; tokens?: BroadcastToken[] }> {
  const response = await fetch(`/api/meeting/${encodeURIComponent(roomName)}/breakouts/broadcast`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, type })
  });
  const result = (await response.json()) as {
    status: boolean;
    message?: string;
    data?: BroadcastStartPayload;
  };
  if (!response.ok || !result.status) throw new Error(result.message || "Broadcast request failed");
  return result.data ?? {};
}

export function useBreakoutMediaBroadcast(roomName: string, serverUrl: string) {
  const auxRoomsRef = useRef<Room[]>([]);
  const publishedTracksRef = useRef<LocalTrack[]>([]);
  const [voiceBroadcastActive, setVoiceBroadcastActive] = useState(false);
  const [shareBroadcastActive, setShareBroadcastActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const teardown = useCallback(async () => {
    for (const aux of auxRoomsRef.current) {
      await aux.disconnect().catch(() => undefined);
    }
    auxRoomsRef.current = [];
    for (const track of publishedTracksRef.current) {
      track.stop();
    }
    publishedTracksRef.current = [];
  }, []);

  const publishVoiceToBreakoutRooms = useCallback(
    async (sessionId: string) => {
      const payload = await postBroadcast(roomName, sessionId, "voice_start");
      const connectUrl = payload.serverUrl || serverUrl;
      const tokens = payload.tokens ?? [];
      if (!connectUrl || !tokens.length) throw new Error("Broadcast tokens were not returned");

      for (const entry of tokens) {
        const track = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true });
        publishedTracksRef.current.push(track);
        const aux = new Room({ adaptiveStream: false, dynacast: false });
        await aux.connect(connectUrl, entry.participantToken);
        await aux.localParticipant.publishTrack(track, { source: Track.Source.Microphone });
        auxRoomsRef.current.push(aux);
      }
    },
    [roomName, serverUrl]
  );

  const publishShareToBreakoutRooms = useCallback(
    async (sessionId: string) => {
      const payload = await postBroadcast(roomName, sessionId, "share_start");
      const connectUrl = payload.serverUrl || serverUrl;
      const tokens = payload.tokens ?? [];
      if (!connectUrl || !tokens.length) throw new Error("Broadcast tokens were not returned");

      const screenTracks = await createLocalScreenTracks({ audio: false });
      publishedTracksRef.current.push(...screenTracks);
      const primaryVideo = screenTracks.find((track) => track.kind === Track.Kind.Video);

      for (const entry of tokens) {
        const aux = new Room({ adaptiveStream: false, dynacast: false });
        await aux.connect(connectUrl, entry.participantToken);
        if (primaryVideo?.mediaStreamTrack) {
          const cloned = new LocalVideoTrack(primaryVideo.mediaStreamTrack.clone());
          publishedTracksRef.current.push(cloned);
          await aux.localParticipant.publishTrack(cloned, { source: Track.Source.ScreenShare });
        }
        auxRoomsRef.current.push(aux);
      }
    },
    [roomName, serverUrl]
  );

  const startVoiceBroadcast = useCallback(
    async (sessionId: string) => {
      if (voiceBroadcastActive || shareBroadcastActive) return;
      setBusy(true);
      try {
        await teardown();
        await publishVoiceToBreakoutRooms(sessionId);
        setVoiceBroadcastActive(true);
      } finally {
        setBusy(false);
      }
    },
    [publishVoiceToBreakoutRooms, shareBroadcastActive, teardown, voiceBroadcastActive]
  );

  const stopVoiceBroadcast = useCallback(
    async (sessionId: string) => {
      if (!voiceBroadcastActive) return;
      setBusy(true);
      try {
        await teardown();
        await postBroadcast(roomName, sessionId, "voice_stop");
        setVoiceBroadcastActive(false);
      } finally {
        setBusy(false);
      }
    },
    [roomName, teardown, voiceBroadcastActive]
  );

  const startShareBroadcast = useCallback(
    async (sessionId: string) => {
      if (voiceBroadcastActive || shareBroadcastActive) return;
      setBusy(true);
      try {
        await teardown();
        await publishShareToBreakoutRooms(sessionId);
        setShareBroadcastActive(true);
      } finally {
        setBusy(false);
      }
    },
    [publishShareToBreakoutRooms, shareBroadcastActive, teardown, voiceBroadcastActive]
  );

  const stopShareBroadcast = useCallback(
    async (sessionId: string) => {
      if (!shareBroadcastActive) return;
      setBusy(true);
      try {
        await teardown();
        await postBroadcast(roomName, sessionId, "share_stop");
        setShareBroadcastActive(false);
      } finally {
        setBusy(false);
      }
    },
    [roomName, shareBroadcastActive, teardown]
  );

  return {
    voiceBroadcastActive,
    shareBroadcastActive,
    broadcastBusy: busy,
    startVoiceBroadcast,
    stopVoiceBroadcast,
    startShareBroadcast,
    stopShareBroadcast,
    stopAllBroadcasts: teardown
  };
}
